import { useContext, useEffect, useState } from "react";
import { AuthContext } from "../../../context/AuthContext";
import { Link } from "react-router-dom";
import api from "../../../utils/axiosInstance";
import { toast } from "../../../components/Toast/toast";
import { exportTable } from "../../../utils/exportTable";
import { btn } from "../../../components/Button/buttonStyles";
import { adjustmentLabel } from "../../../utils/adjustments";

const TYPE_BADGE = {
  flat_rate: { label: "Flat", cls: "bg-amber-100 text-amber-700" },
  unit_based: { label: "Unit", cls: "bg-teal-100 text-teal-700" },
};

const TYPE_STYLES = {
  flat_rate: "border-l-2 border-amber-400",
  unit_based: "border-l-2 border-teal-400",
};

const fmtNum = (v) =>
  v !== undefined && v !== null && v !== "" ? Number(v).toFixed(2) : "—";

const HEADERS = [
  "checkbox",
  "Type",
  "Code",
  "Invoice",
  "Job service",
  "Job language pair",
  "Unit amount",
  "Unit",
  "Price per unit",
  "Subtotal",
  "Extra charge",
  "Discount",
  "Total",
  "Currency",
  "Note for vendor",
  "Job",
  "Job status",
];

const getLangPairFor = (row) => {
  const lp = row.job?.languagePair;
  if (!lp) return "";
  return `${lp.sourceLanguage?.name || ""} → ${lp.targetLanguage?.name || ""}`;
};

// Mirrors HEADERS above, minus the checkbox. Invoice is still a placeholder
// in the table, so it exports empty.
const EXPORT_COLUMNS = [
  { header: "Type", value: (r) => (TYPE_BADGE[r.type] ?? TYPE_BADGE.flat_rate).label },
  { header: "Code", value: (r) => r.payable_code || "" },
  { header: "Invoice", value: () => "" },
  { header: "Job service", value: (r) => r.job?.service?.name || "" },
  { header: "Job language pair", value: (r) => getLangPairFor(r) },
  {
    header: "Unit amount",
    value: (r) => (r.type === "flat_rate" ? "" : fmtNum(r.unit_amount)),
  },
  { header: "Unit", value: (r) => (r.type === "flat_rate" ? "" : r.unit?.name || "") },
  {
    header: "Price per unit",
    value: (r) => (r.type === "flat_rate" ? "" : fmtNum(r.price_per_unit)),
  },
  { header: "Subtotal", value: (r) => fmtNum(r.subtotal) },
  { header: "Extra charge", value: (r) => (r.extra_charge_type ? fmtNum(r.extra_charge) : "") },
  { header: "Discount", value: (r) => (r.discount_type ? fmtNum(r.discount) : "") },
  { header: "Total", value: (r) => fmtNum(r.total) },
  { header: "Currency", value: (r) => r.currency?.currency?.code || "" },
  { header: "Note for vendor", value: (r) => r.note_for_vendor || "" },
  { header: "Job", value: (r) => `LT${r.job_id}` },
  { header: "Job status", value: (r) => r.job?.status || "" },
];

const VendorReceivablesPage = () => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [selected, setSelected] = useState([]);
  const [invoicing, setInvoicing] = useState(false);
  const { user } = useContext(AuthContext);
  const canCreateInvoices = user?.vendor_flags?.can_create_invoices ?? false;

  const fetchData = async () => {
    try {
      setLoading(true);
      const res = await api.get("/vendor-receivables", {
        withCredentials: true,
      });
      setData(res.data.data || []);
    } catch (err) {
      console.error(err);
      setError("Failed to fetch receivables");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const totals = {};
  data.forEach((row) => {
    const code = row.currency?.currency?.code || "";
    if (code) totals[code] = (totals[code] || 0) + Number(row.total || 0);
  });

  const getLangPair = (row) => {
    const lp = row.job?.languagePair;
    if (!lp) return "—";
    return `${lp.sourceLanguage?.name || ""} → ${
      lp.targetLanguage?.name || ""
    }`;
  };

  const isInvoiceable = (row) => row.job?.status === "Completion Accepted";

  const invoiceableData = data.filter(isInvoiceable);

  const isSelected = (row) =>
    selected.some((s) => s.id === row.id && s.type === row.type);

  const toggleSelect = (row) => {
    if (isSelected(row)) {
      setSelected(
        selected.filter((s) => !(s.id === row.id && s.type === row.type))
      );
    } else {
      setSelected([...selected, { id: row.id, type: row.type }]);
    }
  };

  const toggleSelectAll = (e) => {
    if (e.target.checked) {
      setSelected(invoiceableData.map((r) => ({ id: r.id, type: r.type })));
    } else {
      setSelected([]);
    }
  };

  const handleCreateInvoices = async (items) => {
    if (items.length === 0) return;
    setInvoicing(true);
    try {
      const res = await api.post(
        "/vendor-invoices",
        { receivables: items },
        { withCredentials: true }
      );
      const msg = res.data.message || "Invoices created";
      const errCount = res.data.errors?.length || 0;
      toast.error(errCount > 0 ? `${msg}. ${errCount} skipped (see console).` : msg);
      if (res.data.errors) console.warn("Invoice errors:", res.data.errors);
      setSelected([]);
      fetchData();
    } catch (err) {
      console.error(err);
      toast.error(err.response?.data?.message || "Failed to create invoices");
    } finally {
      setInvoicing(false);
    }
  };

  const handleExportToExcel = () => {
    exportTable("my-receivables", EXPORT_COLUMNS, data);
  };

  const allSelected =
    invoiceableData.length > 0 && selected.length === invoiceableData.length;
  const someSelected = selected.length > 0;

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center flex-wrap gap-3">
        <h1 className="text-[22px] font-semibold tracking-tight text-gray-900">Receivables</h1>
        <div className="flex items-center gap-2 text-sm">
          {canCreateInvoices && (
            <>
              <button
                disabled={selected.length === 0 || invoicing}
                onClick={() => handleCreateInvoices(selected)}
                className={btn("primary", "sm")}
              >
                {invoicing ? "Creating..." : "Invoice selected"}
              </button>
              <span className="text-gray-300">|</span>
              <button
                disabled={invoiceableData.length === 0 || invoicing}
                onClick={() =>
                  handleCreateInvoices(
                    invoiceableData.map((r) => ({ id: r.id, type: r.type }))
                  )
                }
                className={btn("primary", "sm")}
              >
                Invoice all
              </button>
            </>
          )}
          <button
            onClick={handleExportToExcel}
            className={btn("secondary", "sm")}
          >
            Export to Excel
          </button>
        </div>
      </div>

      {loading && (
        <div className="flex justify-center py-10">
          <div className="animate-spin rounded-full h-8 w-8 border border-gray-300 border-t-brand-600" />
        </div>
      )}
      {error && <p className="text-danger-600 text-sm">{error}</p>}

      {!loading && !error && (
        <>
          <p className="text-sm text-gray-600">
            Showing <span className="font-semibold">{data.length}</span> items.
          </p>

          <div className="w-full bg-white shadow rounded-lg overflow-hidden">
            <div className="flex items-center gap-4 px-4 py-2 border-b bg-gray-50 text-xs text-gray-500">
              <span className="flex items-center gap-1">
                <span className="inline-block w-2 h-3 rounded-sm bg-amber-400" />
                Flat Rate
              </span>
              <span className="flex items-center gap-1">
                <span className="inline-block w-2 h-3 rounded-sm bg-teal-400" />
                Unit Based
              </span>
              {selected.length > 0 && (
                <span className="ml-auto text-brand-600 font-medium">
                  {selected.length} selected
                </span>
              )}
            </div>

            <div className="max-h-[600px] overflow-y-auto overflow-x-auto">
              <table className="w-full table-auto border-collapse min-w-[1400px]">
                <thead className="sticky top-0 z-10">
                  <tr className="bg-white border-b text-[10px] tracking-widest text-gray-500 uppercase">
                    <th className="px-3 py-2 text-center">
                      <input
                        type="checkbox"
                        checked={allSelected}
                        ref={(el) => {
                          if (el)
                            el.indeterminate = someSelected && !allSelected;
                        }}
                        onChange={toggleSelectAll}
                        className="w-3.5 h-3.5 accent-brand-600 cursor-pointer"
                      />
                    </th>
                    {HEADERS.slice(1).map((h) => (
                      <th
                        key={h}
                        className="px-3 py-2 text-center font-semibold whitespace-nowrap"
                      >
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {data.length === 0 ? (
                    <tr>
                      <td
                        colSpan={HEADERS.length}
                        className="py-10 text-center text-gray-400 text-xs"
                      >
                        No receivables found.
                      </td>
                    </tr>
                  ) : (
                    data.map((row, i) => {
                      const isFlat = row.type === "flat_rate";
                      const badge =
                        TYPE_BADGE[row.type] ?? TYPE_BADGE.flat_rate;
                      const typeStyle = TYPE_STYLES[row.type] ?? "";
                      const currCode = row.currency?.currency?.code || "—";
                      const canInvoice = isInvoiceable(row);
                      const rowSelected = isSelected(row);
                      const rowBg = rowSelected
                        ? "bg-brand-50"
                        : i % 2 === 0
                        ? "bg-gray-50"
                        : "bg-white";

                      return (
                        <tr
                          key={`${row.type}-${row.id}`}
                          className={`${rowBg} ${typeStyle} hover:bg-brand-50 transition-colors`}
                        >
                          <td className="px-3 py-2 text-center">
                            <input
                              type="checkbox"
                              checked={rowSelected}
                              onChange={() => toggleSelect(row)}
                              disabled={!canInvoice}
                              className="w-3.5 h-3.5 accent-brand-600 cursor-pointer disabled:cursor-not-allowed disabled:opacity-40"
                            />
                          </td>
                          <td className="px-3 py-2 text-center whitespace-nowrap">
                            <span
                              className={`inline-block text-[10px] font-semibold px-1.5 py-0.5 rounded ${badge.cls}`}
                            >
                              {badge.label}
                            </span>
                          </td>
                          <td className="px-3 py-2 text-xs whitespace-nowrap text-center">
                            <Link
                              to={`/vendor/receivable/${row.id}?type=${row.type}`}
                              className="text-brand-600 hover:text-brand-800 font-medium underline"
                            >
                              {row.payable_code || "—"}
                            </Link>
                          </td>
                          <td className="px-3 py-2 text-xs text-center text-gray-400">
                            —
                          </td>
                          <td className="px-3 py-2 text-xs text-gray-700">
                            {row.job?.service?.name || "—"}
                          </td>
                          <td className="px-3 py-2 text-xs whitespace-nowrap text-gray-700">
                            {getLangPair(row)}
                          </td>
                          <td className="px-3 py-2 text-xs text-right text-gray-700">
                            {isFlat ? (
                              <span className="text-gray-300">—</span>
                            ) : (
                              fmtNum(row.unit_amount)
                            )}
                          </td>
                          <td className="px-3 py-2 text-xs text-gray-700">
                            {isFlat ? (
                              <span className="text-gray-300">—</span>
                            ) : (
                              row.unit?.name || "—"
                            )}
                          </td>
                          <td className="px-3 py-2 text-xs text-right text-gray-700">
                            {isFlat ? (
                              <span className="text-gray-300">—</span>
                            ) : (
                              fmtNum(row.price_per_unit)
                            )}
                          </td>
                          <td className="px-3 py-2 text-xs text-right text-gray-700">
                            {fmtNum(row.subtotal)}
                          </td>
                          <td className="px-3 py-2 text-xs text-right text-gray-700">
                            {adjustmentLabel(row, "extra_charge")}
                          </td>
                          <td className="px-3 py-2 text-xs text-right text-gray-700">
                            {adjustmentLabel(row, "discount")}
                          </td>
                          <td className="px-3 py-2 text-xs text-right font-semibold whitespace-nowrap text-gray-900">
                            {fmtNum(row.total)}
                          </td>
                          <td className="px-3 py-2 text-xs text-center text-gray-700">
                            {currCode}
                          </td>
                          <td
                            className="px-3 py-2 text-xs max-w-[200px] truncate text-gray-700"
                            title={row.note_for_vendor || ""}
                          >
                            {row.note_for_vendor || "—"}
                          </td>
                          <td className="px-3 py-2 text-xs whitespace-nowrap text-center">
                            <Link
                              to={`/vendor/job/${row.job_id}`}
                              className="text-brand-600 hover:text-brand-800 underline font-medium"
                            >
                              LT{row.job_id}
                            </Link>
                          </td>
                          <td className="px-3 py-2 text-xs whitespace-nowrap text-gray-700">
                            {row.job?.status || "—"}
                          </td>
                        </tr>
                      );
                    })
                  )}

                  {data.length > 0 && Object.keys(totals).length > 0 && (
                    <tr className="bg-gray-50 font-semibold border-t-2">
                      <td colSpan={12} className="px-3 py-2"></td>
                      <td className="px-3 py-2 text-right text-xs">
                        {Object.entries(totals).map(([code, total]) => (
                          <div key={code}>{fmtNum(total)}</div>
                        ))}
                      </td>
                      <td className="px-3 py-2 font-bold text-xs text-center">
                        {Object.keys(totals).map((code) => (
                          <div key={code}>{code}</div>
                        ))}
                      </td>
                      <td colSpan={3}></td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default VendorReceivablesPage;
